﻿/** @import {CompilerOptions} from 'typescript' */

import { createLogging } from './utils.js';
import {
	createConnection,
	createServer,
	createTypeScriptProject,
} from '@volar/language-server/node';
import { createCompileErrorDiagnosticPlugin } from './compileErrorDiagnosticPlugin.js';
import { createDefinitionPlugin } from './definitionPlugin.js';
import { createHoverPlugin } from './hoverPlugin.js';
import { createCompletionPlugin } from './completionPlugin.js';
import { createAutoInsertPlugin } from './autoInsertPlugin.js';
import { createTypeScriptDiagnosticFilterPlugin } from './typescriptDiagnosticPlugin.js';
import { createDocumentHighlightPlugin } from './documentHighlightPlugin.js';
import { createDocumentSymbolPlugin } from './documentSymbolPlugin.js';
import { getRippleLanguagePlugin, resolveConfig } from '@tsrx/typescript-plugin/src/language.js';
import { createTypeScriptServices } from './typescriptService.js';
import { create as createCssService } from 'volar-service-css';

const { log, logError } = createLogging('[Ripple Language Server]');

/**
 * Strip whole-document formatting capabilities from a Volar service plugin.
 *
 * The bundled TypeScript (`typescript-syntactic`) and CSS services advertise a
 * `documentFormattingProvider`. Because they run against the virtual TS/CSS code
 * rather than the `.tsrx` source, their edits don't map back and formatting is a
 * no-op — yet the capability still makes the language client contribute a
 * "TSRX for VS Code" entry to "Format Document With…" that silently does nothing.
 * Formatting for `.tsrx` is owned by Prettier + @tsrx/prettier-plugin (configured
 * as the default `[ripple]` formatter in the VS Code extension), so we drop these
 * capabilities to keep Prettier as the single, working formatter. On-type
 * formatting is left intact.
 *
 * @template {{ capabilities?: Record<string, unknown> }} T
 * @param {T} plugin
 * @returns {T}
 */
function stripDocumentFormatting(plugin) {
	const {
		documentFormattingProvider: _fmt,
		documentRangeFormattingProvider: _rangeFmt,
		...capabilities
	} = plugin.capabilities ?? {};
	return { ...plugin, capabilities };
}

export function createRippleLanguageServer() {
	const connection = createConnection();
	const server = createServer(connection);

	connection.listen();

	// Create language plugin instance once and reuse it
	// This prevents creating multiple instances if the callback is called multiple times
	const rippleLanguagePlugin = getRippleLanguagePlugin();
	log('Language plugin instance created');

	/** @type {WeakSet<Function>} */
	const wrappedFunctions = new WeakSet();

	/**
	 * Ensure TypeScript hosts always see compiler options with Ripple defaults.
	 * @param {unknown} target
	 * @param {string} method
	 */
	function wrapCompilerOptionsProvider(target, method) {
		if (!target) {
			return;
		}

		const host = /** @type {{ [key: string]: unknown }} */ (target);
		const original = host[method];
		if (typeof original !== 'function' || wrappedFunctions.has(original)) {
			return;
		}

		/** @type {CompilerOptions | undefined} */
		let cachedInput;
		/** @type {CompilerOptions | undefined} */
		let cachedOutput;

		const wrapped = () => {
			/** @type {CompilerOptions} */
			const input = original.call(host);
			if (cachedInput !== input) {
				cachedInput = input;
				cachedOutput = resolveConfig({ options: input }).options;
			}
			return cachedOutput;
		};

		wrappedFunctions.add(original);
		wrappedFunctions.add(wrapped);
		host[method] = wrapped;
	}

	connection.onInitialize(async (params) => {
		try {
			log('Initializing Ripple language server...');
			log('Initialization options:', JSON.stringify(params.initializationOptions, null, 2));

			const ts = require('typescript');

			const initResult = server.initialize(
				params,
				createTypeScriptProject(ts, undefined, ({ projectHost }) => {
					wrapCompilerOptionsProvider(projectHost, 'getCompilationSettings');

					return {
						languagePlugins: [rippleLanguagePlugin],
						setup({ project }) {
							wrapCompilerOptionsProvider(
								project?.typescript?.languageServiceHost,
								'getCompilationSettings',
							);
						},
					};
				}),
				[
					createAutoInsertPlugin(),
					createCompletionPlugin(),
					createCompileErrorDiagnosticPlugin(),
					createDefinitionPlugin(),
					createDocumentSymbolPlugin(),
					stripDocumentFormatting(createCssService()),
					...createTypeScriptServices(ts).map(stripDocumentFormatting),
					// !IMPORTANT 'createTypeScriptDiagnosticFilterPlugin', 'createHoverPlugin',
					// and 'createDocumentHighlightPlugin' must come after TypeScript services
					// to intercept volar's and vscode default providers
					createTypeScriptDiagnosticFilterPlugin(),
					createHoverPlugin(),
					createDocumentHighlightPlugin(),
				],
			);

			log('Server initialization complete');
			return initResult;
		} catch (initError) {
			logError('Server initialization failed:', initError);
			throw initError;
		}
	});

	connection.onInitialized(async () => {
		log('Server initialized.');
		server.initialized();

		// Register file watchers for TypeScript/JavaScript files so the language
		// server is notified when they change on disk. Without this, changes to
		// .ts files that are imported by .tsrx files are not detected, causing
		// stale diagnostics until the server is restarted.
		try {
			await server.fileWatcher.watchFiles([
				'**/*.ts',
				'**/*.tsx',
				'**/*.cts',
				'**/*.mts',
				'**/*.js',
				'**/*.jsx',
				'**/*.cjs',
				'**/*.mjs',
				'**/*.d.ts',
				'**/tsconfig.json',
				'**/jsconfig.json',
			]);
			log('File watchers registered for TypeScript/JavaScript files.');
		} catch (err) {
			logError('Failed to register file watchers:', err);
		}
	});

	process.on('uncaughtException', (err) => {
		logError('Uncaught exception:', err);
	});

	process.on('unhandledRejection', (reason, promise) => {
		logError('Unhandled rejection at:', promise, 'reason:', reason);
	});

	return { connection, server };
}
